import { useState, type FormEvent } from 'react';
import { InvalidCodeError, MfaPendingError, RateLimitedError, api } from './api';
import Hint from './Hint';

interface MfaChallengeProps {
  onVerified: () => void;
  onCancel: () => void;
}

/**
 * شاشة الرمز بعد كلمة السر — عربية RTL.
 *
 * الجلسة هنا **جزئية**: الخادم أصدرها بـ `mfa_satisfied = false` وعمرها دقائق،
 * ولا تصل أى مسار محمى. فلا شىء يُحمَّل فى الخلفية من هذه الشاشة، والمخرج
 * الوحيد غير التحقق هو الخروج.
 */
export default function MfaChallenge({ onVerified, onCancel }: MfaChallengeProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await api.mfaVerify(code.trim());
      setCode('');
      onVerified();
    } catch (caught) {
      if (caught instanceof InvalidCodeError) {
        setError('الرمز غير صحيح أو انتهت صلاحيته. جرّب الرمز الحالى من التطبيق.');
      } else if (caught instanceof MfaPendingError) {
        setError('انتهت مهلة التحقق. سجّل الدخول من جديد.');
      } else if (caught instanceof RateLimitedError) {
        setError('محاولات كثيرة. انتظر ' + caught.retryAfterSeconds + ' ثانية ثم أعد المحاولة.');
      } else {
        setError('تعذر الاتصال بالخدمة. تأكد أن الـ API يعمل.');
      }
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={handleSubmit}>
        <div className="logo">
          حسابات<span> • </span>نواة
        </div>
        <h1>التحقق بخطوتين</h1>
        <p className="sub">اكتب الرمز الظاهر فى تطبيق المصادقة، أو أحد أكواد الاسترداد.</p>

        <label htmlFor="code">
          رمز التحقق
          <Hint>الأرقام الستة الظاهرة الآن فى تطبيق المصادقة على موبايلك — تتغير كل ٣٠ ثانية.</Hint>
        </label>
        <input
          id="code"
          name="code"
          type="text"
          dir="ltr"
          inputMode="text"
          autoComplete="one-time-code"
          autoFocus
          required
          minLength={6}
          maxLength={32}
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="primary" disabled={submitting}>
          {submitting ? 'جارى التحقق…' : 'تحقق'}
        </button>

        <button type="button" className="ghost" onClick={onCancel} disabled={submitting}>
          إلغاء والخروج
        </button>

        <p className="sub login-hint">
          الرمز يتغير كل ٣٠ ثانية. كود الاسترداد يُستخدم مرة واحدة فقط.
        </p>
      </form>
    </div>
  );
}
